import { memo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRightIcon } from '@radix-ui/react-icons'

import { PaytrieAPI } from '@paytrie/client'
import { formatMoney } from '@paytrie/common/utils'
import { formatCurrency, formatDate } from '../dashboardDisplayUtils'
import { OtcStatusBadge } from './OtcStatusBadge'
import { trackOtcRequestOpened } from '@/utils/analytics'

type OtcRequestCardProps = {
  index: number
  request: PaytrieAPI.OtcRequest
}

export const OtcRequestCard = memo(function OtcRequestCard({
  index,
  request,
}: OtcRequestCardProps) {
  const { t } = useTranslation()
  const handleOpen = useCallback(
    () =>
      trackOtcRequestOpened({
        otcRequestId: request.id,
        status: request.status,
        position: index,
      }),
    [request.id, request.status, index]
  )

  return (
    <Link
      to={`/otc-requests/${request.id}`}
      onClick={handleOpen}
      title={t('otcRequestRows.viewDetailsTooltip')}
      className="flex flex-col gap-2 rounded-2xl border border-gray-200 bg-cloudLighter p-4 dark:border-gray-700 dark:bg-darkCharcoal"
      data-testid={`card-otc-request-${index}`}
    >
      <div className="flex items-center justify-between gap-3">
        <span
          className="text-xs text-gray-500 dark:text-gray-400"
          data-testid={`card-otc-request-${index}-date`}
        >
          {formatDate(request.createdAt)}
        </span>
        <OtcStatusBadge
          status={request.status}
          cancelledAt={request.cancelledAt}
          testId={`card-otc-request-${index}-status`}
        />
      </div>
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[15px] font-semibold text-black dark:text-white">
        <span className="whitespace-nowrap">
          {formatMoney(request.leftSideValue)}{' '}
          {formatCurrency(request.leftSideLabel)}
        </span>
        <ArrowRightIcon className="h-4 w-4 shrink-0 text-gray-400" />
        <span className="whitespace-nowrap">
          {formatCurrency(request.rightSideLabel)}
        </span>
      </div>
      <span
        className="break-all font-mono text-xs text-gray-400 dark:text-gray-500"
        data-testid={`card-otc-request-${index}-id`}
      >
        {request.id}
      </span>
    </Link>
  )
})
